import styled from "@emotion/styled";
import BusinessIcon from "@mui/icons-material/Business";
import CableIcon from "@mui/icons-material/Cable";
import DescriptionIcon from "@mui/icons-material/Description";
import HistoryIcon from "@mui/icons-material/History";
import LogoutIcon from "@mui/icons-material/Logout";
import PeopleIcon from "@mui/icons-material/People";
import ScienceIcon from "@mui/icons-material/Science";
import {
	AppBar,
	Box,
	Button,
	Drawer,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Stack,
	type Theme,
	Toolbar,
	Typography,
} from "@mui/material";
import { useUserInfoQuery } from "Auth/Api/Queries/useUserInfoQuery";
import { useSignOut } from "Auth/Hooks/useSignOut";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { AppRouting } from "Infrastructure/Utils/UrlUtils";
import { UnstyledLink } from "Shared/Routing/UnstyledLink";
import {
	type FC,
	type PropsWithChildren,
	createContext,
	useContext,
	useEffect,
} from "react";

type Props = {
	title: string;
};

const drawerWidth = 240;

const StyledBox = styled(Box)`
  display: flex;
`;

const StyledAppBar = styled(AppBar)(
	({ theme }: { theme: Theme }) => `
  z-index: ${theme.zIndex.drawer + 1};
`,
);

const StyledDrawer = styled(Drawer)`
  width: ${drawerWidth}px;
  flex-shrink: 0;

  & .MuiDrawer-paper {
    width: ${drawerWidth}px;
    box-sizing: border-box;
  }

  .MuiTypography-root {
    color: ${(props) => props.theme.palette.text.primary};
  }
`;

const StyledMainContent = styled(Box)`
  flex-grow: 1;
  padding: 16px;
`;

const StyledListItem = styled(ListItem)`
  padding: 0;
`;

export const AuthenticatedLayout: FC<PropsWithChildren<Props>> = ({
	title,
	children,
}) => {
	const { t } = useResource();

	useEffect(() => {
		document.title = `${title} | ${t(Resources.Common.AppName)}`;
	}, [title, t]);

	const { data } = useUserInfoQuery();

	const userLogin = data?.status === 200 ? data.data.login : "";
	const tenantName = data?.status === 200 ? data.data.tenantName : "";
	const accessRightCodes =
		data?.status === 200 ? data.data.accessRightCodes : [];

	const menuItems = [
		{
			text: "Extraction Configurations",
			icon: <DescriptionIcon />,
			to: AppRouting.Configurations.path(),
		},
		{
			text: "Test",
			icon: <ScienceIcon />,
			to: AppRouting.Test.path(),
		},
		// Only show admin menu items for users with Tenants access right
		...(accessRightCodes.includes("Tenants")
			? [
					{
						text: "Connector Configurations",
						icon: <CableIcon />,
						to: AppRouting.ConnectorConfigurations.path(),
					},
					{
						text: "Extractions",
						icon: <HistoryIcon />,
						to: AppRouting.Extractions.path(),
					},
					{
						text: "Tenants",
						icon: <BusinessIcon />,
						to: AppRouting.Tenants.path(),
					},
					{
						text: "Users",
						icon: <PeopleIcon />,
						to: AppRouting.Users.path(),
					},
				]
			: []),
	];
	const signOut = useSignOut();

	return (
		<StyledBox>
			{
				<>
					<StyledAppBar position="fixed">
						<Toolbar>
							<Stack
								direction="row"
								justifyContent="space-between"
								alignItems="center"
								width="100%"
							>
								<Typography variant="h6" noWrap component="div">
									DocInsight
								</Typography>
								<Stack
									direction={"row"}
									justifyContent="flex-end"
									alignItems="center"
									spacing={3}
								>
									<Stack
										direction="row"
										alignItems="center"
										spacing={1.5}
										sx={{
											bgcolor: "rgba(255, 255, 255, 0.1)",
											padding: "8px 16px",
											borderRadius: "8px",
										}}
									>
										<Stack
											direction="column"
											alignItems="flex-start"
											spacing={0.5}
										>
											<Typography
												variant="body2"
												sx={{
													fontWeight: 600,
													color: "white",
													fontSize: "0.9rem",
												}}
											>
												{userLogin}
											</Typography>
											<Typography
												variant="body2"
												sx={{
													color: "rgba(255, 255, 255, 0.85)",
													fontSize: "0.8rem",
													fontWeight: 500,
												}}
											>
												{tenantName}
											</Typography>
										</Stack>
									</Stack>
									<Button
										color="inherit"
										onClick={() => signOut()}
										startIcon={<LogoutIcon />}
									>
										{t(Resources.Common.SignOut)}
									</Button>
								</Stack>
							</Stack>
						</Toolbar>
					</StyledAppBar>
					<StyledDrawer variant="permanent">
						<Toolbar />
						<Box
							sx={{
								overflow: "auto",
							}}
						>
							<List>
								{menuItems.map((item) => (
									<UnstyledLink to={item.to} key={item.text}>
										<StyledListItem disablePadding>
											<ListItemButton>
												<ListItemIcon>{item.icon}</ListItemIcon>
												<ListItemText primary={item.text} />
											</ListItemButton>
										</StyledListItem>
									</UnstyledLink>
								))}
							</List>
						</Box>
					</StyledDrawer>
				</>
			}

			<StyledMainContent>
				<Toolbar />
				{children}
			</StyledMainContent>
		</StyledBox>
	);
};
